import React, { useState, useMemo } from 'react';
import {
  Star,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  Calendar,
  Hash,
  ArrowUpRight,
  ArrowDownLeft,
  Briefcase,
  Users,
  Video,
  MapPin,
} from 'lucide-react';
import { SectionCard, MetricCard, TabLoading, TabError, TabEmpty } from './shared';
import { useUserReviews } from '../../hooks/useUserTabs';
import type { BookingReviewItem, GroupReviewItem, ReviewItem, ReviewsBucket } from '../../types/userTabs';
import { formatDateTime, formatRelativeTime } from '../../utils/helpers';

interface ReviewsTabProps {
  userId: number;
}

// ============================================================================
// Helpers
// ============================================================================

function getAllReviews(bucket: ReviewsBucket | null): ReviewItem[] {
  if (!bucket) return [];
  const all: ReviewItem[] = [
    ...(bucket.asProvider || []),
    ...(bucket.asCustomer || []),
    ...(bucket.asGroupHost || []),
    ...(bucket.asGroupParticipant || []),
  ];
  return all.sort((a, b) => new Date(b.ratedAt).getTime() - new Date(a.ratedAt).getTime());
}

function computeAvg(items: ReviewItem[]): number | null {
  if (items.length === 0) return null;
  const sum = items.reduce((acc, r) => acc + r.rating, 0);
  return Math.round((sum / items.length) * 10) / 10;
}

function isBookingReview(r: ReviewItem): r is BookingReviewItem {
  return r.type === 'booking';
}

function isGroupReview(r: ReviewItem): r is GroupReviewItem {
  return r.type === 'group_session';
}

function getRoleBadge(role: string): { label: string; color: string } {
  switch (role) {
    case 'provider': return { label: 'As Provider', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' };
    case 'customer': return { label: 'As Customer', color: 'bg-violet-500/10 text-violet-400 border-violet-500/20' };
    case 'host': return { label: 'As Host', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' };
    case 'participant': return { label: 'As Participant', color: 'bg-teal-500/10 text-teal-400 border-teal-500/20' };
    default: return { label: role, color: 'bg-muted text-muted-foreground border-border/50' };
  }
}

function getModeBadge(mode: string): { label: string; icon: React.ElementType } {
  switch (mode) {
    case 'VIRTUAL': return { label: 'Virtual', icon: Video };
    case 'IN_PERSON': return { label: 'In Person', icon: MapPin };
    default: return { label: mode, icon: Briefcase };
  }
}

// ============================================================================
// Sub-components
// ============================================================================

const StarRating: React.FC<{ rating: number }> = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`h-3.5 w-3.5 ${
          i <= rating
            ? 'text-amber-400 fill-amber-400'
            : i - 0.5 <= rating
              ? 'text-amber-400 fill-amber-400/50'
              : 'text-muted-foreground/30'
        }`}
      />
    ))}
    <span className="ml-1.5 text-sm font-semibold text-foreground">{rating}</span>
  </div>
);

const BoolIndicator: React.FC<{ label: string; value: boolean | null | undefined }> = ({ label, value }) => {
  if (value === null || value === undefined) return null;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${
      value
        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
        : 'bg-red-500/10 text-red-400 border-red-500/20'
    }`}>
      {value ? <ThumbsUp className="h-3 w-3" /> : <ThumbsDown className="h-3 w-3" />}
      {label}
    </span>
  );
};

const ReviewCard: React.FC<{ review: ReviewItem; direction: 'given' | 'received' }> = ({ review, direction }) => {
  const otherUser = direction === 'given' ? review.reviewee : review.reviewer;
  const roleBadge = getRoleBadge(review.role);

  return (
    <div className="p-4 rounded-lg border border-border/50 bg-muted/5 space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {otherUser?.profileImage ? (
              <img src={otherUser.profileImage} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="text-xs font-semibold text-primary">
                {(otherUser?.name || '?').charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate">
              {otherUser?.name || 'Unknown User'}
            </p>
            {otherUser?.id && (
              <p className="text-[11px] text-muted-foreground font-mono">#{otherUser.id}</p>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <StarRating rating={review.rating} />
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${roleBadge.color}`}>
            {roleBadge.label}
          </span>
        </div>
      </div>

      {/* Review text */}
      {review.review && (
        <p className="text-sm text-muted-foreground leading-relaxed pl-12 italic">
          &ldquo;{review.review}&rdquo;
        </p>
      )}

      {/* Boolean indicators */}
      {isBookingReview(review) && (
        <div className="flex flex-wrap gap-2 pl-12">
          <BoolIndicator label="Good Fit" value={review.goodFit} />
          <BoolIndicator label="Would Book Again" value={review.wouldBookAgain} />
          <BoolIndicator label="Would Serve Again" value={review.wouldServeAgain} />
          <BoolIndicator label="Would Refer" value={review.wouldRefer} />
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 pl-12 text-[11px] text-muted-foreground">
        {isBookingReview(review) && (
          <>
            {(() => {
              const mode = getModeBadge(review.serviceMode);
              const ModeIcon = mode.icon;
              return (
                <span className="flex items-center gap-1">
                  <ModeIcon className="h-3 w-3" />
                  {mode.label}
                </span>
              );
            })()}
            <span className="flex items-center gap-1">
              <Hash className="h-3 w-3" />
              Booking {review.bookingId}
            </span>
          </>
        )}
        {isGroupReview(review) && (
          <span className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            {review.groupSession.name}
          </span>
        )}
        <span className="flex items-center gap-1" title={formatDateTime(review.ratedAt)}>
          <Calendar className="h-3 w-3" />
          {formatRelativeTime(review.ratedAt)}
        </span>
      </div>
    </div>
  );
};

// Bucket section (e.g., "As Provider", "As Customer")
const BucketSection: React.FC<{
  title: string;
  icon: React.ElementType;
  reviews: ReviewItem[];
  direction: 'given' | 'received';
}> = ({ title, icon: Icon, reviews, direction }) => {
  if (reviews.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {title} ({reviews.length})
        </p>
      </div>
      {reviews.map((review, idx) => {
        const key = isBookingReview(review)
          ? `booking-${review.bookingId}`
          : `group-${(review as GroupReviewItem).groupSession?.id}-${idx}`;
        return <ReviewCard key={key} review={review} direction={direction} />;
      })}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const ReviewsTab: React.FC<ReviewsTabProps> = ({ userId }) => {
  const { data, loading, error, refetch } = useUserReviews(userId, true);
  const [activeSection, setActiveSection] = useState<'received' | 'given'>('received');

  const allReceived = useMemo(() => getAllReviews(data?.received ?? null), [data]);
  const allGiven = useMemo(() => getAllReviews(data?.given ?? null), [data]);
  const avgReceived = useMemo(() => computeAvg(allReceived), [allReceived]);
  const avgGiven = useMemo(() => computeAvg(allGiven), [allGiven]);

  if (loading) return <TabLoading />;
  if (error) return <TabError message={error} onRetry={refetch} />;
  if (!data) return <TabEmpty message="No reviews data found" icon={Star} />;

  const received = data.received;
  const given = data.given;
  const receivedCount = allReceived.length;
  const givenCount = allGiven.length;

  return (
    <div className="space-y-5">
      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Reviews Received"
          value={receivedCount}
          icon={ArrowDownLeft}
          color="text-blue-400"
          bgColor="bg-blue-500/10"
        />
        <MetricCard
          label="Avg Received"
          value={avgReceived !== null ? String(avgReceived) : 'N/A'}
          icon={Star}
          color="text-amber-400"
          bgColor="bg-amber-500/10"
        />
        <MetricCard
          label="Reviews Given"
          value={givenCount}
          icon={ArrowUpRight}
          color="text-emerald-400"
          bgColor="bg-emerald-500/10"
        />
        <MetricCard
          label="Avg Given"
          value={avgGiven !== null ? String(avgGiven) : 'N/A'}
          icon={Star}
          color="text-violet-400"
          bgColor="bg-violet-500/10"
        />
      </div>

      {/* Toggle */}
      <div className="flex gap-1 p-1 rounded-lg bg-muted/30 w-fit">
        <button
          onClick={() => setActiveSection('received')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
            activeSection === 'received'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <ArrowDownLeft className="h-3.5 w-3.5 inline mr-1.5" />
          Received ({receivedCount})
        </button>
        <button
          onClick={() => setActiveSection('given')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
            activeSection === 'given'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <ArrowUpRight className="h-3.5 w-3.5 inline mr-1.5" />
          Given ({givenCount})
        </button>
      </div>

      {/* Received */}
      {activeSection === 'received' && (
        <SectionCard
          title="Reviews Received"
          icon={ArrowDownLeft}
          subtitle={`${receivedCount} review${receivedCount !== 1 ? 's' : ''} from others about this user`}
        >
          {receivedCount === 0 ? (
            <div className="flex flex-col items-center py-8 gap-2">
              <MessageSquare className="h-6 w-6 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No reviews received yet</p>
            </div>
          ) : (
            <div className="space-y-6">
              <BucketSection
                title="As Provider"
                icon={Briefcase}
                reviews={received?.asProvider || []}
                direction="received"
              />
              <BucketSection
                title="As Customer"
                icon={Star}
                reviews={received?.asCustomer || []}
                direction="received"
              />
              <BucketSection
                title="As Group Host"
                icon={Users}
                reviews={received?.asGroupHost || []}
                direction="received"
              />
            </div>
          )}
        </SectionCard>
      )}

      {/* Given */}
      {activeSection === 'given' && (
        <SectionCard
          title="Reviews Given"
          icon={ArrowUpRight}
          subtitle={`${givenCount} review${givenCount !== 1 ? 's' : ''} this user wrote about others`}
        >
          {givenCount === 0 ? (
            <div className="flex flex-col items-center py-8 gap-2">
              <MessageSquare className="h-6 w-6 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No reviews given yet</p>
            </div>
          ) : (
            <div className="space-y-6">
              <BucketSection
                title="As Customer"
                icon={Star}
                reviews={given?.asCustomer || []}
                direction="given"
              />
              <BucketSection
                title="As Provider"
                icon={Briefcase}
                reviews={given?.asProvider || []}
                direction="given"
              />
              <BucketSection
                title="As Group Participant"
                icon={Users}
                reviews={given?.asGroupParticipant || []}
                direction="given"
              />
            </div>
          )}
        </SectionCard>
      )}
    </div>
  );
};

export default ReviewsTab;
